import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus, Clock, MapPin, ChevronRight as ChevR } from "lucide-react";
import { motion } from "framer-motion";
import { AddEventDialog, EventDetailDialog } from "@/components/EntityDialogs";

const catColor = {
  School: "bg-indigo-100 text-indigo-700 border-indigo-200",
  Travel: "bg-sky-100 text-sky-700 border-sky-200",
  Health: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Family: "bg-amber-100 text-amber-700 border-amber-200",
  Social: "bg-rose-100 text-rose-700 border-rose-200",
  Household: "bg-slate-100 text-slate-700 border-slate-200",
};
const catDot = {
  School: "bg-indigo-500", Travel: "bg-sky-500", Health: "bg-emerald-500",
  Family: "bg-amber-500", Social: "bg-rose-500", Household: "bg-slate-500",
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfWeek = (d) => { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };
const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const VIEWS = [["month", "Month"], ["week", "Week"], ["day", "Day"], ["agenda", "Agenda"]];

export default function CalendarPage() {
  const { events } = useFgp();
  const today = new Date();
  // Start where the data lives so events are visible immediately, else today.
  const initial = events.length ? new Date(events.map((e) => e.date).sort()[0]) : today;
  const [cursor, setCursor] = useState(initial);
  const [selected, setSelected] = useState(initial);
  const [view, setView] = useState("month");
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [selEvent, setSelEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const openEvent = (e) => { setSelEvent(e); setDetailOpen(true); };
  const openAdd = (d) => { setAddDate(d ? ymd(d) : ""); setAddOpen(true); };

  const eventsOn = (d) => events.filter((e) => sameDay(new Date(e.date), d))
    .sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const shift = (dir) => {
    const c = new Date(cursor);
    if (view === "month" || view === "agenda") c.setMonth(c.getMonth() + dir);
    else if (view === "week") c.setDate(c.getDate() + dir * 7);
    else c.setDate(c.getDate() + dir);
    setCursor(c);
    if (view === "day") setSelected(c);
  };
  const goToday = () => { setCursor(new Date()); setSelected(new Date()); };

  const headerLabel =
    view === "week"
      ? (() => { const s = startOfWeek(cursor); const e = new Date(s); e.setDate(s.getDate() + 6); return `${s.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`; })()
      : view === "day"
        ? cursor.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  // ----- Month grid cells -----
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const offset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const weekDays = (() => { const s = startOfWeek(cursor); return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; }); })();

  const EventPill = ({ e, onClick }) => (
    <button onClick={onClick} data-testid={`agenda-event-${e.id}`}
      className={`w-full text-left rounded-md border-l-2 px-2 py-1 text-xs transition-all hover:shadow ${catColor[e.category] || catColor.Household}`}>
      <span className="font-semibold truncate block">{e.title}</span>
      <span className="text-[10px] opacity-80">{e.time}</span>
    </button>
  );

  return (
    <div>
      <PageHeader
        title="Calendar"
        subtitle="Shared schedule — school, travel, health, custody and social."
        actions={<Button onClick={() => openAdd(selected)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-event"><Plus className="h-4 w-4" /> Add event</Button>}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => shift(1)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today">Today</Button>
          <h2 className="font-heading text-lg font-semibold text-slate-900 ml-2" data-testid="cal-label">{headerLabel}</h2>
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {VIEWS.map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${view === v ? "bg-neutral-600 text-white" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* MONTH */}
      {view === "month" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayLabels.map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-400 py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                const dayEvents = d ? eventsOn(d) : [];
                const isSel = d && sameDay(d, selected);
                const isToday = d && sameDay(d, today);
                return (
                  <button key={i} disabled={!d} onClick={() => d && setSelected(d)}
                    data-testid={d ? `cal-day-${d.getDate()}` : undefined}
                    className={`aspect-square rounded-lg p-1.5 text-left relative transition-colors ${!d ? "" : isSel ? "bg-neutral-600 text-white" : "hover:bg-slate-50 border border-transparent hover:border-slate-200"}`}>
                    {d && (
                      <>
                        <span className={`text-xs font-medium inline-flex items-center justify-center h-5 w-5 rounded-full ${isToday && !isSel ? "bg-neutral-200 text-neutral-800" : isSel ? "text-white" : "text-slate-600"}`}>{d.getDate()}</span>
                        {dayEvents.length > 0 && (
                          <div className="flex gap-0.5 mt-1 flex-wrap">
                            {dayEvents.slice(0, 3).map((e, j) => (
                              <span key={j} className={`h-1.5 w-1.5 rounded-full ${isSel ? "bg-white" : catDot[e.category] || catDot.Household}`} />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-heading font-semibold text-slate-900 mb-1">{selected.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long" })}</h3>
            <p className="text-xs text-slate-400 mb-4">{eventsOn(selected).length} event(s)</p>
            <div className="space-y-3">
              {eventsOn(selected).map((e) => (
                <motion.button key={e.id} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} onClick={() => openEvent(e)}
                  className={`w-full text-left rounded-lg border-l-2 p-3 transition-all hover:shadow-md hover:-translate-y-0.5 ${catColor[e.category] || catColor.Household}`} data-testid={`day-event-${e.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800">{e.title}</p>
                    <ChevR className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Clock className="h-3 w-3" /> {e.time}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location} · {e.person}</p>
                </motion.button>
              ))}
              {eventsOn(selected).length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-slate-400 mb-3">No events on this day.</p>
                  <Button variant="outline" size="sm" onClick={() => openAdd(selected)} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add event</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WEEK */}
      {view === "week" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[700px]">
            {weekDays.map((d) => {
              const dayEvents = eventsOn(d);
              const isToday = sameDay(d, today);
              return (
                <div key={d.toISOString()} className="min-h-[260px]">
                  <button onClick={() => openAdd(d)} className="w-full text-center pb-2 mb-2 border-b border-slate-100 group" data-testid={`week-col-${d.getDate()}`}>
                    <p className="text-xs text-slate-400">{d.toLocaleDateString("en-GB", { weekday: "short" })}</p>
                    <p className={`font-heading text-lg font-semibold ${isToday ? "text-neutral-700" : "text-slate-800"}`}>{d.getDate()}</p>
                  </button>
                  <div className="space-y-1.5">
                    {dayEvents.map((e) => <EventPill key={e.id} e={e} onClick={() => openEvent(e)} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DAY */}
      {view === "day" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 max-w-2xl">
          <div className="space-y-3">
            {eventsOn(cursor).map((e) => (
              <button key={e.id} onClick={() => openEvent(e)} data-testid={`day-event-${e.id}`}
                className={`w-full text-left rounded-lg border-l-2 p-3.5 transition-all hover:shadow-md ${catColor[e.category] || catColor.Household}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-800">{e.title}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/60">{e.category}</span>
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Clock className="h-3 w-3" /> {e.time}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location} · {e.person}</p>
              </button>
            ))}
            {eventsOn(cursor).length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-slate-400 mb-3">Nothing scheduled for this day.</p>
                <Button variant="outline" size="sm" onClick={() => openAdd(cursor)} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add event</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AGENDA */}
      {view === "agenda" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="divide-y divide-slate-100">
            {[...events]
              .filter((e) => new Date(e.date).getMonth() === cursor.getMonth() && new Date(e.date).getFullYear() === cursor.getFullYear())
              .sort((a, b) => a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""))
              .map((e) => (
                <button key={e.id} onClick={() => openEvent(e)} data-testid={`upcoming-event-${e.id}`}
                  className="w-full text-left flex items-center gap-4 py-3 hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors">
                  <div className="text-center w-12 flex-shrink-0">
                    <p className="text-xs text-slate-400">{new Date(e.date).toLocaleDateString("en-GB", { month: "short" })}</p>
                    <p className="font-heading text-lg font-semibold text-slate-800 leading-none">{new Date(e.date).getDate()}</p>
                  </div>
                  <span className={`h-2 w-2 rounded-full ${catDot[e.category] || catDot.Household}`} />
                  <div className="flex-1"><p className="text-sm font-medium text-slate-800">{e.title}</p><p className="text-xs text-slate-400">{e.time} · {e.person}</p></div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${catColor[e.category] || catColor.Household}`}>{e.category}</span>
                </button>
              ))}
            {events.filter((e) => new Date(e.date).getMonth() === cursor.getMonth() && new Date(e.date).getFullYear() === cursor.getFullYear()).length === 0 && (
              <p className="text-sm text-slate-400 py-12 text-center">No events this month.</p>
            )}
          </div>
        </div>
      )}

      <AddEventDialog open={addOpen} onOpenChange={setAddOpen} defaultDate={addDate} />
      <EventDetailDialog open={detailOpen} onOpenChange={setDetailOpen} event={selEvent} />
    </div>
  );
}
